import { motion } from "framer-motion";

import { Section, SectionHead, Chip } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { ENGINEERING_LAYERS } from "../data/capabilities";

/**
 * Engineering surface area, drawn as a stack rather than listed as a wall
 * of logos.
 *
 * The brief's instruction was "do not simply list technologies, show them
 * as an interconnected engineering system", and the honest way to do that
 * is the shape a real system actually has: layers, in order, with the
 * concerns that cut across all of them drawn as cutting across all of
 * them. An engineer reads this diagram and can tell whether we have built
 * anything. A list of framework names tells them nothing.
 */

const CROSS_CUTTING = [
  "Multi-tenancy",
  "Audit trails",
  "Data residency",
  "Access control",
  "Cost ceilings",
];

export function Engineering() {
  return (
    <Section id="engineering">
      <div className="container">
        <SectionHead
          eyebrow="Capability"
          title={
            <>
              Deep engineering.{" "}
              <span className="text-gradient">Practical outcomes.</span>
            </>
          }
          lede="We work across the whole stack, but the part that decides whether a system survives is rarely the part on the brochure. It is isolation, evidence, and what happens when the network drops."
        />

        <div className="mt-10 grid gap-5 lg:grid-cols-[1fr_260px] lg:gap-6 sm:mt-14">
          {/* The stack */}
          <div className="relative">
            {/* Rail */}
            <motion.div
              initial={{ scaleY: 0 }}
              whileInView={{ scaleY: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: "top" }}
              className="absolute left-[7px] top-2 hidden h-[calc(100%-1rem)] w-px bg-gradient-to-b from-emerald-400/70 via-teal-400/50 to-violet-500/70 sm:block"
            />

            <div className="space-y-3 sm:pl-10">
              {ENGINEERING_LAYERS.map((layer, i) => (
                <motion.div
                  key={layer.layer}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: i * 0.09 }}
                  className="group relative"
                >
                  <span className="absolute -left-10 top-5 hidden size-[15px] place-items-center rounded-full border border-white/15 bg-ink-950 sm:grid">
                    <span className="size-[7px] rounded-full bg-gradient-to-br from-emerald-400 to-violet-500 transition-transform duration-300 group-hover:scale-125" />
                  </span>

                  <div className="glass p-5 transition-colors duration-300 hover:border-white/20 sm:p-6">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <h3 className="font-display text-base font-semibold tracking-tight sm:text-lg">
                        {layer.layer}
                      </h3>
                      <span className="text-xs text-white/40">{layer.note}</span>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {layer.items.map((item) => (
                        <Chip key={item}>{item}</Chip>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Cross-cutting concerns — drawn beside the stack because that is
           *  where they belong: in every layer, not in one of them. */}
          <Reveal delay={0.2}>
            <div className="glass relative flex h-full flex-col justify-between overflow-hidden p-5 sm:p-6">
              <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-emerald-400/50 to-transparent" />
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/40">
                  Runs through every layer
                </div>
                <p className="mt-3 text-sm leading-relaxed text-white/60">
                  These are not features bolted on before launch. They are
                  decisions made in the first week, because retrofitting any of
                  them means rewriting the data model.
                </p>
              </div>
              <ul className="mt-6 space-y-2.5">
                {CROSS_CUTTING.map((c, i) => (
                  <motion.li
                    key={c}
                    initial={{ opacity: 0, x: -6 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.25 + i * 0.06 }}
                    className="flex items-center gap-2.5 text-sm text-white/75"
                  >
                    <span className="size-1 rounded-full bg-emerald-400" />
                    {c}
                  </motion.li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </Section>
  );
}
